/**
 * SUB-4.4 · FR38. What the account bar above an open record says, and when it
 * says anything at all.
 *
 * Design A1, chosen 2026-08-13 (`docs/stories/SUB-4.0-phase-3-shared-context.md`
 * §9, D21): a bar ABOVE the record rather than a chip in the title row, because
 * FR38 asks for attribution that is persistent and prominent at EVERY viewport
 * width and a title-row chip wraps or truncates at 380px.
 *
 * ⚠️ THE DECISION IS HERE AND THE RENDER IS IN THE COMPONENT, so the rule can be
 * tested without a DOM. `RecordAccountBar.tsx` renders whatever this returns and
 * decides nothing.
 *
 * ⚠️ THIS FILE BOUNDS NOTHING. Same standing as `accountAttribution.ts`: which
 * records a caller may open is `projects_select`, and which they may save is
 * `projects_update` + `projects_update_merged`. Everything here runs after the
 * row has already arrived.
 */

import { accountLabelFor, type AccountLabel } from '@/lib/accountAttribution';
import { isMergedScope, type MergedScopeStatus } from '@/lib/mergedScope';

export interface RecordAccountBarState {
  /**
   * Whether the bar renders at all.
   *
   * ⚠️ FR38 IS A MERGED-SCOPE REQUIREMENT, NOT A GLOBAL ONE. In single-account
   * scope every open record belongs to the one account the header already names,
   * so a bar there would be a permanent restatement of the obvious, and the
   * banner that means "you are working in someone else's account" would stop
   * meaning it.
   */
  visible: boolean;
  /** The account the record belongs to. Never empty when `visible`. */
  label: AccountLabel;
  /**
   * Whether this record belongs to an account OTHER than the one every
   * unwidened surface resolves to (FR33's single account, which in merged scope
   * is the anchor).
   *
   * The bar renders for every record in merged scope, because "this one is
   * yours" is as much a piece of attribution as "this one is a client's". The
   * flag only changes the emphasis.
   */
  isOtherAccount: boolean;
  /** Screen-reader text. Read out in full rather than inferred from styling. */
  announcement: string;
}

export const HIDDEN_ACCOUNT_BAR: RecordAccountBarState = {
  visible: false,
  label: { tenantId: '', name: '', known: false },
  isOtherAccount: false,
  announcement: '',
};

/**
 * ⚠️ AN UNRESOLVED SCOPE HIDES THE BAR, and that is the safe direction here even
 * though it is the opposite of a control failing closed. The bar is a LABEL, not
 * a gate: showing it wrongly would announce the wrong account, which is worse
 * than showing nothing, and nothing is what single scope shows anyway.
 * `isMergedScope` already treats unresolved as not-merged.
 */
export function recordAccountBarState(
  recordTenantId: string | null | undefined,
  scope: MergedScopeStatus,
  names: ReadonlyMap<string, string>,
): RecordAccountBarState {
  if (!isMergedScope(scope)) return HIDDEN_ACCOUNT_BAR;

  const label = accountLabelFor(recordTenantId, names);

  // Compared against the SINGLE account, not against the anchor field, because
  // those are the same thing today (D25) and the day they stop being, the
  // question the bar answers is "is this record outside the account the rest of
  // the app is showing me", which is the single account by definition.
  const isOtherAccount =
    !!recordTenantId && recordTenantId !== scope.singleAccountTenantId;

  return {
    visible: true,
    label,
    isOtherAccount,
    announcement: isOtherAccount
      ? `This record belongs to ${label.name}, a different account from the one you are working in.`
      : `This record belongs to ${label.name}.`,
  };
}
